import { createElement } from '../utils';
import { MarkdownFormatter } from '../utils/markdown';

/**
 * Preview modal options
 */
interface PreviewModalOptions {
  content: string;
  theme?: 'light' | 'dark';
  onClose: () => void;
}

/**
 * Preview modal to display formatted comment before posting
 * Shows Markdown rendered as HTML in a modal overlay
 */
export class PreviewModal {
  private overlay: HTMLElement;
  private modal: HTMLElement;
  private options: PreviewModalOptions;
  private markdownFormatter: MarkdownFormatter;

  constructor(options: PreviewModalOptions) {
    this.options = {
      theme: 'light',
      ...options
    };
    
    this.markdownFormatter = MarkdownFormatter.getInstance();
    this.overlay = this.createOverlay();
    this.modal = this.createModal();
    this.setupEventListeners();
    this.addStyles();
  }

  /**
   * Create modal overlay
   */
  private createOverlay(): HTMLElement {
    const overlay = createElement('div', {
      className: 'komentas-preview-overlay',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'komentas-preview-title'
    });

    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.close();
      }
    });

    return overlay;
  }

  /**
   * Create modal content
   */
  private createModal(): HTMLElement {
    const modal = createElement('div', {
      className: 'komentas-preview-modal'
    });

    // Header
    const header = createElement('div', {
      className: 'komentas-preview-header'
    });

    const title = createElement('h3', {
      id: 'komentas-preview-title',
      className: 'komentas-preview-title'
    }, ['Preview Comment']);

    const closeBtn = createElement('button', {
      className: 'komentas-preview-close',
      type: 'button',
      'aria-label': 'Close preview'
    }, ['×']) as HTMLButtonElement;

    closeBtn.addEventListener('click', () => this.close());

    header.appendChild(title);
    header.appendChild(closeBtn);

    // Content area
    const contentArea = createElement('div', {
      className: 'komentas-preview-content'
    });

    // Render Markdown to HTML
    const renderedHtml = this.markdownFormatter.toHtml(this.options.content);
    
    if (renderedHtml) {
      contentArea.innerHTML = renderedHtml;
    } else {
      const emptyMessage = createElement('p', {
        className: 'komentas-preview-empty'
      }, ['Nothing to preview. Start typing to see formatted content.']);
      contentArea.appendChild(emptyMessage);
    }

    // Footer with info
    const footer = createElement('div', {
      className: 'komentas-preview-footer'
    });

    const info = createElement('p', {
      className: 'komentas-preview-info'
    }, ['This is how your comment will appear after posting.']);

    const closeFooterBtn = createElement('button', {
      className: 'komentas-preview-btn',
      type: 'button'
    }, ['Close Preview']) as HTMLButtonElement;

    closeFooterBtn.addEventListener('click', () => this.close());

    footer.appendChild(info);
    footer.appendChild(closeFooterBtn);

    // Assemble modal
    modal.appendChild(header);
    modal.appendChild(contentArea);
    modal.appendChild(footer);

    return modal;
  }

  /**
   * Setup event listeners
   */
  private setupEventListeners(): void {
    // Close on Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      }
    };

    document.addEventListener('keydown', handleEscape);
    
    // Store handler for cleanup
    (this.overlay as any).__escapeHandler = handleEscape;
  }

  /**
   * Add CSS styles
   */
  private addStyles(): void {
    const isDark = this.options.theme === 'dark';
    
    const styles = `
      .komentas-preview-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        padding: 20px;
        animation: fadeIn 0.2s ease-in;
      }

      .komentas-preview-modal {
        background: ${isDark ? '#2a2a2a' : '#ffffff'};
        border-radius: 8px;
        max-width: 700px;
        width: 100%;
        max-height: 80vh;
        display: flex;
        flex-direction: column;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        animation: slideUp 0.3s ease-out;
      }

      .komentas-preview-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid ${isDark ? '#444' : '#e1e5e9'};
      }

      .komentas-preview-title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: ${isDark ? '#fff' : '#333'};
      }

      .komentas-preview-close {
        background: none;
        border: none;
        font-size: 32px;
        line-height: 1;
        color: ${isDark ? '#888' : '#666'};
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s;
      }

      .komentas-preview-close:hover {
        background: ${isDark ? '#444' : '#f0f0f0'};
        color: ${isDark ? '#fff' : '#333'};
      }

      .komentas-preview-close:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 124, 186, 0.4);
      }

      .komentas-preview-content {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        color: ${isDark ? '#e0e0e0' : '#333'};
        line-height: 1.6;
      }

      /* Markdown content styles for preview */
      .komentas-preview-content p {
        margin: 0 0 12px 0;
      }

      .komentas-preview-content p:last-child {
        margin-bottom: 0;
      }

      .komentas-preview-content strong,
      .komentas-preview-content b {
        font-weight: 600;
        color: ${isDark ? '#fff' : '#000'};
      }

      .komentas-preview-content em,
      .komentas-preview-content i {
        font-style: italic !important;
      }

      .komentas-preview-content a {
        color: #007cba;
        text-decoration: none;
        border-bottom: 1px solid transparent;
        transition: border-color 0.2s;
      }

      .komentas-preview-content a:hover {
        border-bottom-color: #007cba;
      }

      .komentas-preview-content ul,
      .komentas-preview-content ol {
        margin: 12px 0;
        padding-left: 28px;
      }

      .komentas-preview-content li {
        margin: 6px 0;
      }

      .komentas-preview-content code {
        background: ${isDark ? '#1a1a1a' : '#f5f5f5'};
        border: 1px solid ${isDark ? '#444' : '#e1e5e9'};
        border-radius: 3px;
        padding: 2px 6px;
        font-family: 'Courier New', Courier, monospace;
        font-size: 13px;
        color: ${isDark ? '#e0e0e0' : '#c33'};
      }

      .komentas-preview-content pre {
        background: ${isDark ? '#1a1a1a' : '#f5f5f5'};
        border: 1px solid ${isDark ? '#444' : '#e1e5e9'};
        border-radius: 4px;
        padding: 12px;
        overflow-x: auto;
        margin: 12px 0;
      }

      .komentas-preview-content pre code {
        background: none;
        border: none;
        padding: 0;
        color: ${isDark ? '#e0e0e0' : '#333'};
      }

      .komentas-preview-content blockquote {
        border-left: 4px solid ${isDark ? '#555' : '#ddd'};
        margin: 12px 0;
        padding: 12px 16px;
        background: ${isDark ? '#1a1a1a' : '#f9f9f9'};
        color: ${isDark ? '#ccc' : '#555'};
        font-style: italic;
      }

      .komentas-preview-content h4,
      .komentas-preview-content h5,
      .komentas-preview-content h6 {
        margin: 16px 0 12px 0;
        font-weight: 600;
        color: ${isDark ? '#fff' : '#000'};
      }

      .komentas-preview-content h4 {
        font-size: 18px;
      }

      .komentas-preview-content h5 {
        font-size: 16px;
      }

      .komentas-preview-content h6 {
        font-size: 14px;
      }

      .komentas-preview-empty {
        color: ${isDark ? '#888' : '#666'};
        font-style: italic;
        text-align: center;
        padding: 40px 20px;
      }

      .komentas-preview-footer {
        padding: 16px 20px;
        border-top: 1px solid ${isDark ? '#444' : '#e1e5e9'};
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
      }

      .komentas-preview-info {
        margin: 0;
        font-size: 13px;
        color: ${isDark ? '#888' : '#666'};
        flex: 1;
      }

      .komentas-preview-btn {
        background: #007cba;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s;
      }

      .komentas-preview-btn:hover {
        background: #005a8b;
      }

      .komentas-preview-btn:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(0, 124, 186, 0.4);
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slideUp {
        from { 
          opacity: 0;
          transform: translateY(20px);
        }
        to { 
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (max-width: 600px) {
        .komentas-preview-overlay {
          padding: 0;
        }

        .komentas-preview-modal {
          max-width: 100%;
          max-height: 100vh;
          border-radius: 0;
        }

        .komentas-preview-footer {
          flex-direction: column;
          align-items: stretch;
        }

        .komentas-preview-info {
          text-align: center;
        }

        .komentas-preview-btn {
          width: 100%;
        }
      }
    `;

    // Check if styles already exist
    if (!document.getElementById('komentas-preview-modal-styles')) {
      const styleSheet = createElement('style', { 
        id: 'komentas-preview-modal-styles' 
      });
      styleSheet.textContent = styles;
      document.head.appendChild(styleSheet);
    }
  }

  /**
   * Show the preview modal
   */
  public show(): void {
    this.overlay.appendChild(this.modal);
    document.body.appendChild(this.overlay);
    
    // Focus the close button for accessibility
    const closeBtn = this.modal.querySelector('.komentas-preview-close') as HTMLButtonElement;
    if (closeBtn) {
      closeBtn.focus();
    }
  }

  /**
   * Close the preview modal
   */
  public close(): void {
    // Remove escape key handler
    const handler = (this.overlay as any).__escapeHandler;
    if (handler) {
      document.removeEventListener('keydown', handler);
    }

    // Remove from DOM
    this.overlay.remove();
    
    // Call onClose callback
    this.options.onClose();
  }

  /**
   * Static factory method to show preview
   */
  public static show(content: string, theme: 'light' | 'dark' = 'light'): PreviewModal {
    const modal = new PreviewModal({
      content,
      theme,
      onClose: () => {
        // Modal will be removed from DOM
      }
    });
    
    modal.show();
    return modal;
  }
}
